import { pb, fileUrl } from "@/lib/pocketbase";
import { pbRepository } from "./pocketbase-repository";
import { auth } from "./auth.service";
import type { Material } from "@/types/models";

const repo = pbRepository<Material>("materials");

export const materialsService = {
  /**
   * Materialien in Anzeigereihenfolge. `position` gewinnt; Altbestand ohne
   * Wert (und damit `position === undefined`) wandert nach hinten und wird
   * dort nach Anlagedatum sortiert. Dieselbe Reihenfolge bestimmt später die
   * Seitenfolge im Arbeits-PDF (siehe workspace.service.ts).
   */
  async listByTopic(topicId: string): Promise<Material[]> {
    const items = await repo.list({
      filter: pb.filter("topic = {:t}", { t: topicId }),
      sort: "created",
    });
    return materialsService.sortForDisplay(items);
  },

  sortForDisplay(items: Material[]): Material[] {
    // PocketBase liefert für ein leeres Zahlenfeld 0 statt undefined.
    const rank = (m: Material) =>
      m.position || Date.parse((m.created ?? "").replace(" ", "T")) || Number.MAX_SAFE_INTEGER;
    return items.sort((a, b) => {
      const pa = rank(a);
      const pb_ = rank(b);
      if (pa !== pb_) return pa - pb_;
      return (a.created ?? "").localeCompare(b.created ?? "");
    });
  },

  /**
   * Neue Reihenfolge festschreiben. Geschrieben wird nur, was sich wirklich
   * geändert hat – ein Umsortieren von zwei Zeilen soll nicht zwanzig
   * Schreibvorgänge auslösen.
   *
   * Der geschriebene Wert landet dabei auch auf dem übergebenen Datensatz.
   * Ohne das vergleicht der nächste Aufruf gegen den Stand von vor dem ersten
   * Umsortieren und überspringt Zeilen, die sich sehr wohl geändert haben:
   * beim zweiten Ziehen in derselben Sitzung bekamen dann zwei Materialien
   * dieselbe Position, und die Reihenfolge sprang scheinbar zurück.
   */
  async reorder(orderedIds: readonly string[], current: readonly Material[]): Promise<void> {
    const byId = new Map(current.map((m) => [m.id, m]));
    await Promise.all(
      orderedIds.map(async (id, index) => {
        const m = byId.get(id);
        const position = index + 1;
        if (!m || m.position === position) return;
        await repo.update(id, { position } as Partial<Material>);
        m.position = position;
      })
    );
  },

  get(id: string): Promise<Material> {
    return repo.getOne(id);
  },

  upload(topicId: string, file: File, title?: string, onProgress?: (fraction: number) => void): Promise<Material> {
    const fd = new FormData();
    fd.append("owner", auth.currentUser()?.id ?? "");
    fd.append("topic", topicId);
    fd.append("title", title?.trim() || file.name);
    fd.append("file", file);
    fd.append("type", file.type || "");
    fd.append("size", String(file.size));
    fd.append("position", String(Date.now()));
    return repo.create(fd, onProgress);
  },

  /** Wie `upload()`, aber für selbst erzeugte Bytes (kein `File`-Objekt/OS-Dateiname nötig). */
  uploadBlob(topicId: string, blob: Blob, fileName: string, title: string, position = Date.now()): Promise<Material> {
    const fd = new FormData();
    fd.append("owner", auth.currentUser()?.id ?? "");
    fd.append("topic", topicId);
    fd.append("title", title);
    fd.append("file", blob, fileName);
    fd.append("type", blob.type || "");
    fd.append("size", String(blob.size));
    fd.append("position", String(position));
    return repo.create(fd);
  },

  update(id: string, data: Partial<Material>): Promise<Material> {
    return repo.update(id, data);
  },

  saveDetails(
    id: string,
    data: { title: string; file?: Blob; fileName?: string }
  ): Promise<Material> {
    if (!data.file) return repo.update(id, { title: data.title });

    const fd = new FormData();
    fd.append("title", data.title);
    // Dateiname als drittes Argument statt über ein `File`-Objekt – das
    // funktioniert in jedem WebView gleich.
    fd.append("file", data.file, data.fileName ?? `${data.title}.pdf`);
    fd.append("type", data.file.type || "");
    fd.append("size", String(data.file.size));
    return repo.update(id, fd);
  },

  remove(id: string): Promise<void> {
    return repo.remove(id);
  },

  /** Öffentliche/abgesicherte Datei-URL für Vorschau/Download. */
  urlFor(material: Material): string {
    return fileUrl(material as unknown as Record<string, unknown>, material.file);
  },

  /**
   * Löst die Markdown-Kurzform `<recordId>/<datei>` in eine echte Datei-URL
   * auf – ohne den Record zu laden (PocketBase baut die URL rein aus
   * Collection, Id und Dateinamen).
   */
  urlForRef(ref: string): string {
    const slash = ref.indexOf("/");
    if (slash < 1) return "";
    const id = ref.slice(0, slash);
    const filename = ref.slice(slash + 1);
    return fileUrl({ id, collectionId: "materials", collectionName: "materials" }, filename);
  },
};
